// src/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, googleProvider, db } from './firebaseConfig';
import {
  onAuthStateChanged,
  signOut,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { requestNotificationPermission } from './utils/notifications';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // ✅ Allow if email is verified or if using Google provider (which is always verified)
        const isVerified = firebaseUser.emailVerified || (firebaseUser.providerData.length > 0 && firebaseUser.providerData[0].providerId === 'google.com');

        if (isVerified) {
          setUser(firebaseUser);
          requestNotificationPermission();

          // Sync user to Firestore
          try {
            const userRef = doc(db, 'users', firebaseUser.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
              // Create new profile
              const newProfile = {
                uid: firebaseUser.uid,
                displayName: firebaseUser.displayName || 'Anonymous',
                email: firebaseUser.email,
                photoURL: firebaseUser.photoURL,
                elo: 1200,
                bio: 'Just a chess fan.',
                createdAt: serverTimestamp(),
                lastLogin: serverTimestamp(),
                isAdmin: false // Default to false
              };
              await setDoc(userRef, newProfile);
              setUser({ ...firebaseUser, ...newProfile });
            } else {
              // Update last login
              const updateData = { lastLogin: serverTimestamp() };
              if (firebaseUser.displayName) updateData.displayName = firebaseUser.displayName;
              if (firebaseUser.photoURL) updateData.photoURL = firebaseUser.photoURL;

              await setDoc(userRef, updateData, { merge: true });

              // Merge Firestore data with Auth data
              setUser({ ...firebaseUser, ...userSnap.data() });
            }
          } catch (error) {
            console.error("Error syncing user profile:", error);
            // FALLBACK TO SUCCESS: Still log the user into the app even if Firestore sync fails!
            setUser(firebaseUser);
          }

        } else {
          // 👋 Sign out if not verified
          signOut(auth);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = () => signOut(auth);

  const login = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Google Login Error:', err.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
